import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { decryptData } from "../utils/crypto";
import { useQueryClient } from "@tanstack/react-query";

function UpdateUserInfo({ user, loginState, setLoginSuccess }) {
  const navigate = useNavigate();
  const userID = user?.id || null;

  const queryClient = useQueryClient();

  const [profileData, setProfileData] = useState({
    email: "",
    name: "",
    phone: "",
    city: "",
    state: "",
    country: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userID) return;
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile/user`,
          {
            params: { userID },
            withCredentials: true,
          }
        );
        const userData = decryptData(response.data.encryptedData);
        console.log("Response in updateUserProfile: ", userData);

        const fetchedData = userData;

        setProfileData({
          email: fetchedData.email || "",
          name: fetchedData.name || "",
          phone: fetchedData.phone || "",
          city: fetchedData.city || "",
          state: fetchedData.state || "",
          country: fetchedData.country || "",
        });

        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching user data: ", err);
        setError(err);
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [userID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!userID) {
      console.log("No userID found, cannot save profile!");
      return;
    }

    console.log("LoginState from props: ", loginState);
    let newLoginState;

    if (loginState.includes("ready to move to setup profile")) {
      newLoginState = "User prepare to login";
    }

    try {
      const payload = {
        userID,
        name: profileData.name,
        phone: profileData.phone,
        city: profileData.city,
        state: profileData.state,
        country: profileData.country,
        newLoginState,
      };

      const result = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/update-user-data`,
        payload,
        { withCredentials: true }
      );
      console.log("Profile updated successfully!", result);

      toast.success("Profile updated successfully!");
      queryClient.invalidateQueries(["userData", userID]);

      if (setLoginSuccess) {
        setLoginSuccess(true);
      } else {
        navigate("/dashboard", { state: { message: "First time logging in" } });
      }
    } catch (err) {
      console.error(
        "Error updating profile:",
        err.response?.data?.message || err.message
      );
      toast.error("Error updating profile!");
    }
  };

  if (isLoading) return <div>Loading profile...</div>;
  if (error) return <div>Error fetching profile data.</div>;

  return (
    <>
      <h2 className="text-2xl font-bold text-center mb-4">
        Complete Your Profile
      </h2>
      <form onSubmit={handleProfileSave} className="space-y-4">
        <div>
          <label className="block font-medium">Email</label>
          <input
            type="email"
            name="email"
            value={profileData.email}
            onChange={handleInputChange}
            placeholder="Enter Email"
            className="w-full border border-gray-300 p-3 rounded-md"
            required
            disabled
          />
        </div>
        <div>
          <label className="block font-medium">Name</label>
          <input
            type="text"
            name="name"
            value={profileData.name}
            onChange={handleInputChange}
            placeholder="Enter Name"
            className="w-full border border-gray-300 p-3 rounded-md"
            required
          />
        </div>
        <div>
          <label className="block font-medium">Phone</label>
          <input
            type="text"
            name="phone"
            value={profileData.phone}
            onChange={handleInputChange}
            placeholder="Enter Phone"
            className="w-full border border-gray-300 p-3 rounded-md"
            required
          />
        </div>
        <div>
          <label className="block font-medium">City</label>
          <input
            type="text"
            name="city"
            value={profileData.city}
            onChange={handleInputChange}
            placeholder="Enter City"
            className="w-full border border-gray-300 p-3 rounded-md"
            required
          />
        </div>
        <div>
          <label className="block font-medium">State</label>
          <input
            type="text"
            name="state"
            value={profileData.state}
            onChange={handleInputChange}
            placeholder="Enter State"
            className="w-full border border-gray-300 p-3 rounded-md"
            required
          />
        </div>
        <div>
          <label className="block font-medium">Country</label>
          <input
            type="text"
            name="country"
            value={profileData.country}
            onChange={handleInputChange}
            placeholder="Enter Country"
            className="w-full border border-gray-300 p-3 rounded-md"
            required
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600"
          >
            Save Profile
          </button>
        </div>
      </form>
    </>
  );
}

UpdateUserInfo.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    username: PropTypes.string.isRequired,
  }).isRequired,
  loginState: PropTypes.string.isRequired,
  setLoginSuccess: PropTypes.func,
};

export default UpdateUserInfo;
